import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { usePromoCodes } from "@/hooks/usePromoCodes";
import { PromoCodeTable } from "@/components/admin/promo/PromoCodeTable";
import { PromoCodeForm } from "@/components/admin/promo/PromoCodeForm";
import { PromoCode } from "@/types/admin";
import { Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";

export default function AdminPromoCodes() {
  const { t } = useTranslation();
  const { getPromoCodes, createPromoCode, updatePromoCode, loading } = usePromoCodes();
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);

  const loadPromoCodes = useCallback(async () => {
    const data = await getPromoCodes();
    setPromoCodes(data);
  }, [getPromoCodes]);

  useEffect(() => {
    loadPromoCodes();
  }, [loadPromoCodes]);

  const handleCreate = async (data: Partial<PromoCode>) => {
    const result = await createPromoCode(data);
    if (result) {
      setShowAddModal(false);
      loadPromoCodes();
    }
  };

  const handleToggleStatus = async (id: string, active: boolean) => {
    await updatePromoCode(id, { is_active: active });
    loadPromoCodes();
  };

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-8 py-8">
      {/* Header Section */}
      <div className="mb-8 flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-4xl font-black tracking-tighter text-[#1A1A1A]">{t('admin.promoCodes.title')}</h2>
          <p className="text-lg font-medium text-gray-500">{t('admin.promoCodes.subtitle')}</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 rounded-xl border-2 border-black bg-[#6FEDD6] px-6 py-3 text-lg font-black shadow-[4px_4px_0_#000] hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_#000] active:translate-x-1 active:translate-y-1 active:shadow-none"
        >
          <Plus className="h-6 w-6" />
          {t('admin.promoCodes.createNew')}
        </button>
      </div>

      <PromoCodeTable 
        promoCodes={promoCodes} 
        loading={loading}
        onDeactivate={(id) => handleToggleStatus(id, false)}
        onActivate={(id) => handleToggleStatus(id, true)}
      />

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl scale-in-center rounded-2xl border-2 border-black bg-[#F5F0E8] p-8 shadow-[8px_8px_0_#1A1A1A]">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="text-2xl font-black">{t('admin.promoCodes.createNew')}</h3>
              <button 
                onClick={() => setShowAddModal(false)}
                title={t('common.close')}
                className="rounded-lg border-2 border-transparent p-1 hover:border-black hover:bg-white/50"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <PromoCodeForm onSubmit={handleCreate} loading={loading} />
          </div>
        </div>
      )}
    </div>
  );
}
